import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { clearFeeSettingsCache } from '../../utils/feeSettings';

const PaymentSettings = () => {
  const { checkPermission } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/payment-settings');
      setSettings(response.data.data);
    } catch (error) {
      console.error('Error loading payment settings:', error);
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!checkPermission('Update Payment Settings')) {
      toast.error('You don\'t have permission to update payment settings');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/payment-settings', settings);
      setSettings(response.data.data);
      toast.success('Payment settings updated successfully');
      clearFeeSettingsCache(); // Clear cache after successful save
    } catch (error) {
      console.error('Error updating payment settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update payment settings');
    } finally {
      setSaving(false);
    }
  };

  const testGateway = async (gateway) => {
    setTesting(prev => ({ ...prev, [gateway]: true }));
    try {
      const response = await api.post(`/payment-settings/test/${gateway}`);
      const result = response.data.data;
      
      if (result.success) {
        toast.success(`${gateway.toUpperCase()} connection test successful: ${result.message}`);
      } else {
        toast.error(`${gateway.toUpperCase()} connection test failed: ${result.message}`);
      }
    } catch (error) {
      console.error(`Error testing ${gateway}:`, error);
      toast.error(`Failed to test ${gateway} connection`);
    } finally {
      setTesting(prev => ({ ...prev, [gateway]: false }));
    }
  };

  // Check permissions after hooks
  if (!checkPermission('View Payment Settings')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view payment settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Payment Settings</h1>
                <p className="text-gray-600">Configure payment gateway integrations</p>
              </div>
              <button
                onClick={handleSave}
                disabled={saving || !checkPermission('Update Payment Settings')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          <div className="px-6 py-6 space-y-8">
            {/* Stripe Settings */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Stripe Configuration</h3>
                  <p className="text-sm text-gray-600">Credit/Debit Cards, UPI, Net Banking</p>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.stripe.enabled}
                      onChange={(e) => handleInputChange('stripe', 'enabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enabled</span>
                  </label>
                  <button
                    onClick={() => testGateway('stripe')}
                    disabled={testing.stripe || !settings.stripe.enabled}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {testing.stripe ? 'Testing...' : 'Test'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secret Key
                  </label>
                  <input
                    type="password"
                    value={settings.stripe.secretKey}
                    onChange={(e) => handleInputChange('stripe', 'secretKey', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="sk_test_..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Publishable Key
                  </label>
                  <input
                    type="text"
                    value={settings.stripe.publishableKey}
                    onChange={(e) => handleInputChange('stripe', 'publishableKey', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="pk_test_..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook Secret
                  </label>
                  <input
                    type="password"
                    value={settings.stripe.webhookSecret}
                    onChange={(e) => handleInputChange('stripe', 'webhookSecret', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="whsec_..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={settings.stripe.webhookUrl}
                    onChange={(e) => handleInputChange('stripe', 'webhookUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://yourdomain.com/api/webhooks/stripe"
                  />
                  <p className="text-xs text-gray-500 mt-1">Public URL for Stripe webhooks</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mode
                  </label>
                  <select
                    value={settings.stripe.mode}
                    onChange={(e) => handleInputChange('stripe', 'mode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="test">Test</option>
                    <option value="live">Live</option>
                  </select>
                </div>
              </div>
            </div>

            {/* PayPal Settings */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">PayPal Configuration</h3>
                  <p className="text-sm text-gray-600">PayPal Account, Credit/Debit Cards</p>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.paypal.enabled}
                      onChange={(e) => handleInputChange('paypal', 'enabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enabled</span>
                  </label>
                  <button
                    onClick={() => testGateway('paypal')}
                    disabled={testing.paypal || !settings.paypal.enabled}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {testing.paypal ? 'Testing...' : 'Test'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={settings.paypal.clientId}
                    onChange={(e) => handleInputChange('paypal', 'clientId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your PayPal Client ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    value={settings.paypal.clientSecret}
                    onChange={(e) => handleInputChange('paypal', 'clientSecret', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your PayPal Client Secret"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook ID
                  </label>
                  <input
                    type="text"
                    value={settings.paypal.webhookId}
                    onChange={(e) => handleInputChange('paypal', 'webhookId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your PayPal Webhook ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={settings.paypal.webhookUrl}
                    onChange={(e) => handleInputChange('paypal', 'webhookUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://yourdomain.com/api/webhooks/paypal"
                  />
                  <p className="text-xs text-gray-500 mt-1">Public URL for PayPal webhooks</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mode
                  </label>
                  <select
                    value={settings.paypal.mode}
                    onChange={(e) => handleInputChange('paypal', 'mode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sandbox">Sandbox</option>
                    <option value="live">Live</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Razorpay Settings */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Razorpay Configuration</h3>
                  <p className="text-sm text-gray-600">UPI, Cards, Net Banking, Wallets</p>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.razorpay.enabled}
                      onChange={(e) => handleInputChange('razorpay', 'enabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enabled</span>
                  </label>
                  <button
                    onClick={() => testGateway('razorpay')}
                    disabled={testing.razorpay || !settings.razorpay.enabled}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {testing.razorpay ? 'Testing...' : 'Test'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key ID
                  </label>
                  <input
                    type="text"
                    value={settings.razorpay.keyId}
                    onChange={(e) => handleInputChange('razorpay', 'keyId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="rzp_test_..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key Secret
                  </label>
                  <input
                    type="password"
                    value={settings.razorpay.keySecret}
                    onChange={(e) => handleInputChange('razorpay', 'keySecret', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Razorpay Key Secret"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook Secret
                  </label>
                  <input
                    type="password"
                    value={settings.razorpay.webhookSecret}
                    onChange={(e) => handleInputChange('razorpay', 'webhookSecret', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Razorpay Webhook Secret"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={settings.razorpay.webhookUrl}
                    onChange={(e) => handleInputChange('razorpay', 'webhookUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://yourdomain.com/api/webhooks/razorpay"
                  />
                  <p className="text-xs text-gray-500 mt-1">Public URL for Razorpay webhooks</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mode
                  </label>
                  <select
                    value={settings.razorpay.mode}
                    onChange={(e) => handleInputChange('razorpay', 'mode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="test">Test</option>
                    <option value="live">Live</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bank Transfer Settings */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Bank Transfer Configuration</h3>
                  <p className="text-sm text-gray-600">NEFT/RTGS/IMPS Transfer</p>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.bankTransfer.enabled}
                      onChange={(e) => handleInputChange('bankTransfer', 'enabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enabled</span>
                  </label>
                  <button
                    onClick={() => testGateway('bank_transfer')}
                    disabled={testing.bank_transfer || !settings.bankTransfer.enabled}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {testing.bank_transfer ? 'Testing...' : 'Test'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={settings.bankTransfer.accountNumber}
                    onChange={(e) => handleInputChange('bankTransfer', 'accountNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Bank Account Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Name
                  </label>
                  <input
                    type="text"
                    value={settings.bankTransfer.accountName}
                    onChange={(e) => handleInputChange('bankTransfer', 'accountName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Account Holder Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={settings.bankTransfer.bankName}
                    onChange={(e) => handleInputChange('bankTransfer', 'bankName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Bank Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    value={settings.bankTransfer.ifscCode}
                    onChange={(e) => handleInputChange('bankTransfer', 'ifscCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="IFSC Code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch
                  </label>
                  <input
                    type="text"
                    value={settings.bankTransfer.branch}
                    onChange={(e) => handleInputChange('bankTransfer', 'branch', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Branch Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    value={settings.bankTransfer.instructions}
                    onChange={(e) => handleInputChange('bankTransfer', 'instructions', e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Instructions for bank transfer"
                  />
                </div>
              </div>
            </div>

            {/* General Settings */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
              
              {/* Fee Showing Setting - Top Priority */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-md font-medium text-blue-900 mb-1">Fee Display Settings</h4>
                    <p className="text-sm text-blue-700">
                      Control whether fee information is visible to users in forms and applications
                    </p>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.general.showFees}
                      onChange={(e) => handleInputChange('general', 'showFees', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                    />
                    <span className="ml-3 text-sm font-medium text-blue-900">
                      {settings.general.showFees ? 'Show Fees' : 'Hide Fees'}
                    </span>
                  </label>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  When disabled, fee information will be calculated in the background but hidden from user view in forms, trademark applications, and submissions.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Currency
                  </label>
                  <select
                    value={settings.general.defaultCurrency}
                    onChange={(e) => handleInputChange('general', 'defaultCurrency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="INR">INR (Indian Rupee)</option>
                    <option value="USD">USD (US Dollar)</option>
                    <option value="EUR">EUR (Euro)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.general.paymentTimeout / (60 * 1000)}
                    onChange={(e) => handleInputChange('general', 'paymentTimeout', e.target.value * 60 * 1000)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="60"
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.general.autoConfirmPayments}
                      onChange={(e) => handleInputChange('general', 'autoConfirmPayments', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Auto-confirm payments</span>
                  </label>
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.general.sendPaymentNotifications}
                      onChange={(e) => handleInputChange('general', 'sendPaymentNotifications', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Send payment notifications</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings; 